import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Alert } from "reactstrap";
import { withRouter } from "react-router-dom";
import Api from "../services/globalBibleApi";
import BreadCrumbs from "../components/BreadCrumbs";

const defaultBibleMeta = {
  disableOffline: false,
  disableFootnotes: false
};

const BibleEdit = ({ location, match }) => {
  const { bibleId } = match.params;
  const [bible, setBible] = useState(null);
  const [bibleMeta, setBibleMeta] = useState(defaultBibleMeta);
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    Api.getBible(bibleId)
      .then(b => setBible(b))
      .catch(console.error); // eslint-disable-line
  }, [bibleId]);

  useEffect(() => {
    const newState = { ...defaultBibleMeta, ...(bible?.meta || {}) };
    setBibleMeta(newState);
  }, [bible]);

  const handleSubmit = e => {
    e.preventDefault();

    Api.updateBible(bibleId, bibleMeta)
      .then(res => {
        setNotifications([{ type: "success", message: res.message }]);
      })
      .catch(err => {
        const message = err.response
          ? err.response.data.message
          : "Error saving bible's metadata";
        setNotifications([{ type: "error", message }]);
      });
  };

  const updateMeta = (key, value) => {
    setBibleMeta({ ...bibleMeta, [key]: value });
  };

  return (
    <div>
      <BreadCrumbs
        title={`Edit Bible${bible ? `: ${bible.name}` : ""}`}
        paths={[
          { title: "Bibles", link: "/bibles" },
          { title: "Edit Bible", link: location.pathname }
        ]}
      />
      {bible && (
        <>
          <div className="bg-white overflow-hidden shadow rounded-lg mb-6">
            <div className="p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Bible Metadata
              </h3>
              <p className="mt-1 text-sm leading-5 text-gray-500">
                Add metadata to a bible to customize it usage accross all
                Global.Bible sites.
              </p>
              <div className="w-full border-t border-gray-200 my-3" />
              <form onSubmit={e => handleSubmit(e)}>
                <div className="relative flex items-start mb-3">
                  <div className="absolute flex items-center h-5">
                    <input
                      id="disableOffline"
                      type="checkbox"
                      className="form-checkbox h-4 w-4 text-blue-600 transition duration-150 ease-in-out"
                      checked={bibleMeta.disableOffline}
                      onChange={() =>
                        updateMeta("disableOffline", !bibleMeta.disableOffline)
                      }
                    />
                  </div>
                  <div className="pl-7 text-sm leading-5">
                    {/* eslint-disable-next-line */}
                    <label
                      htmlFor="disableOffline"
                      className="font-medium text-gray-700"
                    >
                      Disable Offline
                    </label>
                    <p className="text-gray-500">
                      Disable users being able to download this bible offline
                    </p>
                  </div>
                </div>
                <div className="relative flex items-start mb-3">
                  <div className="absolute flex items-center h-5">
                    <input
                      id="disableFootnotes"
                      type="checkbox"
                      className="form-checkbox h-4 w-4 text-blue-600 transition duration-150 ease-in-out"
                      checked={bibleMeta.disableFootnotes}
                      onChange={() =>
                        updateMeta(
                          "disableFootnotes",
                          !bibleMeta.disableFootnotes
                        )
                      }
                    />
                  </div>
                  <div className="pl-7 text-sm leading-5">
                    {/* eslint-disable-next-line */}
                    <label
                      htmlFor="disableFootnotes"
                      className="font-medium text-gray-700"
                    >
                      Disable Footnotes
                    </label>
                    <p className="text-gray-500">
                      Disable footnotes from appearing in the UI for this bible
                    </p>
                  </div>
                </div>
                {notifications.map(({ message, type }) => (
                  <Alert
                    key={message}
                    color={type === "error" ? "danger" : undefined}
                  >
                    {message}
                  </Alert>
                ))}
                <div className="w-full flex justify-end">
                  <span className="inline-flex rounded-md shadow-sm">
                    <button
                      type="submit"
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm hover:no-underline leading-5 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-500 focus:outline-none focus:border-blue-700 focus:shadow-outline-blue active:bg-blue-700 transition ease-in-out duration-150"
                    >
                      Save Changes
                    </button>
                  </span>
                </div>
              </form>
            </div>
          </div>
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-6">
              <h3 className="mb-2 text-lg leading-6 font-medium text-gray-900">
                Raw API.Bible Response
              </h3>
              <pre className="rounded bg-gray-100 p-4">
                {JSON.stringify(bible, null, 2)}
              </pre>
              <br />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

BibleEdit.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      bibleId: PropTypes.string
    })
  }).isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string
  }).isRequired
};

export default withRouter(BibleEdit);
